import React, { useEffect, useRef, useState } from "react";
import {
  X,
  Users,
  Calendar,
  Filter,
  UserPlus,
  Download,
  Pencil,
  Trash2,
  Plus,
} from "lucide-react";
import DeleteUserModal from "./DeleteUserModal";
import EditModal from "./EditModal";
import { deleteGroupData } from "../../../services/groupServices";
import EditGroupModal from "./EditGroupModal";
import ReactModal from "react-modal";

const GroupModal = ({ group, onClose, isOpen, refreshGroups }) => {
  const [activeFilter, setActiveFilter] = useState("all");
  const modalRef = useRef(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedUserName, setSelectedUserName] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [isAdd, setisAdd] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [openEditGroupModal, setOpenEditGroupModal] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [isDataUpdating, setIsDataUpdating] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const getFilteredUsers = () => {
    if (!group?.groupDetails.users) return [];

    if (activeFilter === "all") {
      return group.groupDetails.users;
    }

    return group.groupDetails.users.filter(
      (user) => user.userAdditionType === activeFilter
    );
  };

  const filteredUsers = getFilteredUsers();

  const getFilterStats = () => {
    const totalUsers = group?.groupDetails.users.length || 0;
    return { totalUsers };
  };

  const { totalUsers } = getFilterStats();

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const filterButtons = [
    {
      key: "all",
      label: "All Users",
      count: totalUsers,
      icon: Users,
      gradient: "from-blue-500 to-indigo-600",
    },
  ];

  const handleClickOutside = (event) => {
    if (openEditModal || deleteModal || openEditGroupModal || isDataUpdating) {
      return;
    }

    if (modalRef.current && !modalRef.current.contains(event.target)) {
      onClose();
    }
  };

  useEffect(() => {
    if (group) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    group,
    onClose,
    openEditModal,
    deleteModal,
    openEditGroupModal,
    isDataUpdating,
  ]);

  const handleDeleteUser = async () => {
    if (!selectedUserId || !group?.groupDetails._id) {
      console.error("Missing group or user ID");
      setDeleteModal(false);
      return;
    }

    setIsDeleting(true);
    setIsDataUpdating(true);
    setApiError(null);
    try {
      await deleteGroupData(group.groupDetails._id, selectedUserId);
      setDeleteModal(false);
      setSelectedUserId(null);
      setSelectedUserName("");
      setSuccessMessage("User deleted successfully");
      onClose();
    } catch (error) {
      console.error("Error deleting user:", error);
      setApiError(error?.message || "Failed to delete user");
      setDeleteModal(false);
      setSelectedUserId(null);
      setSelectedUserName("");
    } finally {
      setIsDeleting(false);
      setIsDataUpdating(false);
      refreshGroups();
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteModal(false);
    setSelectedUserId(null);
    setSelectedUserName("");
    onClose();
  };

  const handleEditSuccess = () => {
    setOpenEditModal(false);
    setSelectedUser(null);
    setisAdd(false);
    setIsDataUpdating(true);
    setSuccessMessage("User updated successfully");
    refreshGroups();
    setIsDataUpdating(false);
    onClose();
  };

  const handleGroupEditSuccess = () => {
    setOpenEditGroupModal(false);
    setSelectedData(null);
    setSuccessMessage("Group details updated successfully");
    setIsDataUpdating(true);
    refreshGroups();
    setIsDataUpdating(false);
    onClose();
  };

  const handleEditClose = () => {
    setOpenEditModal(false);
    setSelectedUser(null);
    setisAdd(false);
    onClose();
  };

  const handleGroupEditClose = () => {
    setOpenEditGroupModal(false);
    setSelectedData(null);
    onClose();
  };

  if (!group) return null;

  return (
    <>
      <ReactModal
        isOpen={isOpen}
        onRequestClose={onClose}
        className="bg-white animate-scale-in w-full max-w-4xl h-[85vh] rounded-xl shadow-2xl overflow-hidden flex flex-col"
        overlayClassName="fixed inset-0 z-50 flex items-center justify-center p-2 bg-black/50 backdrop-blur-sm"
        ariaHideApp={false}
      >
        <div ref={modalRef} className="flex flex-col h-full">
          {/* Header - Fixed Height */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-2 text-white flex-shrink-0">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <div className="p-1 bg-white/20 rounded-md backdrop-blur-sm">
                  <Users className="w-4 h-4" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">
                    Group Details
                  </h2>
                  <p className="text-indigo-100 text-xs">
                    Manage your group members
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-1 hover:bg-white/20 rounded-md transition-colors duration-200"
                aria-label="Close modal"
                disabled={isDataUpdating}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-auto">
            <div className="p-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-10 h-28 ">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-2 rounded-lg border border-blue-200 flex items-center">
                  <div className="flex items-center gap-2 w-full">
                    <div className="p-1 bg-blue-500 rounded-md flex-shrink-0">
                      <Users className="w-4 h-4 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-600 font-medium">
                        Group Name
                      </p>
                      <p className="text-sm font-bold text-gray-900 truncate">
                        {group.groupDetails.groupName}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-2 rounded-lg border border-emerald-200 flex items-center">
                  <div className="flex items-center gap-2 w-full">
                    <div className="p-1 bg-emerald-500 rounded-md flex-shrink-0">
                      <Calendar className="w-4 h-4 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-600 font-medium">
                        Created On
                      </p>
                      <p className="text-xs font-semibold text-gray-900 truncate">
                        {formatDate(group.groupDetails.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-2 rounded-lg border border-purple-200 flex items-center">
                  <div className="flex items-center gap-2 w-full">
                    <div className="p-1 bg-purple-500 rounded-md flex-shrink-0">
                      <Filter className="w-4 h-4 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-600 font-medium">
                        Total Members
                      </p>
                      <p className="text-lg font-bold text-gray-900">
                        {totalUsers}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-3 h-10">
                {filterButtons.map((button) => {
                  const Icon = button.icon;
                  const isActive = activeFilter === button.key;

                  return (
                    <button
                      key={button.key}
                      onClick={() => setActiveFilter(button.key)}
                      className={`flex items-center gap-1 px-3 py-1 rounded-lg text-sm font-medium transition-all duration-200 h-8 ${
                        isActive
                          ? `bg-gradient-to-r ${button.gradient} text-white shadow-md`
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                      }`}
                      disabled={isDataUpdating}
                    >
                      <Icon className="w-3 h-3" />
                      <span>{button.label}</span>
                      <span
                        className={`px-1 py-0.5 rounded-full text-xs font-bold ${
                          isActive ? "bg-white/20" : "bg-gray-200"
                        }`}
                      >
                        {button.count}
                      </span>
                    </button>
                  );
                })}

                <button
                  onClick={() => {
                    setSelectedData(group.groupDetails);
                    setOpenEditGroupModal(true);
                  }}
                  className="flex items-center gap-1 px-3 py-1 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200 transition-colors duration-200 h-8"
                  disabled={isDataUpdating}
                >
                  <Pencil className="w-3 h-3" />
                  <span>Edit Group</span>
                </button>
              </div>

              {/* Table - Fixed Height with Scroll */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className="h-80 overflow-auto">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          #
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Member Name
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Phone Number
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Addition Type
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredUsers.length > 0 ? (
                        filteredUsers.map((user, index) => (
                          <tr
                            key={user._id}
                            className="hover:bg-gray-50 transition-colors duration-150 h-12"
                          >
                            <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                              {index + 1}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-xs mr-2">
                                  {user.name.charAt(0).toUpperCase()}
                                </div>
                                <div className="text-sm font-medium text-gray-900 truncate">
                                  {user.name}
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600 font-mono">
                              {user.phone}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  user.userAdditionType === "manual"
                                    ? "bg-emerald-100 text-emerald-800"
                                    : "bg-purple-100 text-purple-800"
                                }`}
                              >
                                {user.userAdditionType === "manual" ? (
                                  <>
                                    <UserPlus className="w-2 h-2 mr-1" />
                                    Manual
                                  </>
                                ) : (
                                  <>
                                    <Download className="w-2 h-2 mr-1" />
                                    Import
                                  </>
                                )}
                              </span>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm">
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setOpenEditModal(true);
                                  }}
                                  className="p-1 rounded-md hover:bg-blue-50 text-blue-600 hover:text-blue-700 transition-colors duration-200"
                                  title="Edit User"
                                  disabled={isDataUpdating}
                                >
                                  <Pencil className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedUserName(user.name);
                                    setSelectedUserId(user._id);
                                    setDeleteModal(true);
                                  }}
                                  className="p-1 rounded-md hover:bg-red-50 text-red-600 hover:text-red-700 transition-colors duration-200"
                                  title="Delete User"
                                  disabled={isDataUpdating}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="px-3 py-8 text-center">
                            <div className="flex flex-col items-center gap-2">
                              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                                <Users className="w-6 h-6 text-gray-400" />
                              </div>
                              <div className="flex flex-col items-center">
                                <p className="text-gray-500 font-medium text-sm">
                                  No members found
                                </p>
                                <p className="text-gray-400 text-xs mb-2">
                                  Add some members to get started
                                </p>
                                <button
                                  onClick={() => {
                                    setOpenEditModal(true);
                                    setisAdd(true);
                                  }}
                                  className="flex items-center gap-1 px-3 py-1 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors duration-200 text-sm"
                                  disabled={isDataUpdating}
                                >
                                  <Plus className="w-3 h-3" />
                                  Add Members
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ReactModal>

      {deleteModal && (
        <DeleteUserModal
          isOpen={deleteModal}
          onClose={handleCloseDeleteModal}
          onConfirm={handleDeleteUser}
          userName={selectedUserName}
          isDeleting={isDeleting}
        />
      )}

      {openEditModal && (
        <EditModal
          isOpen={openEditModal}
          close={handleEditClose}
          group={group?.groupDetails}
          selectedUser={selectedUser}
          isAdd={isAdd}
          onSuccess={handleEditSuccess}
        />
      )}

      {openEditGroupModal && (
        <EditGroupModal
          isOpen={openEditGroupModal}
          close={handleGroupEditClose}
          groupId={group.groupDetails._id}
          existingGroupDetails={selectedData}
          onSuccess={handleGroupEditSuccess}
        />
      )}
    </>
  );
};

export default GroupModal;
